import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ColDef,
  GetContextMenuItemsParams,
  GridReadyEvent,
  ICellRendererParams,
  IServerSideGetRowsParams,
  PaginationChangedEvent,
  SortModelItem,
} from '@ag-grid-community/core'
import { ModuleRegistry } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model'
import { Button, Modal, Select, Tag } from '@surecompanies/core_components'
import Input from 'antd/es/input'
import DatePicker from 'antd/es/date-picker'
import type { Dayjs } from 'dayjs'
import Tooltip from 'antd/es/tooltip'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import { useDebounce } from 'hooks/useDebounce'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import { PlanOverrideAction } from '../../enums/plan-override-action.enum'
import type {
  PlanLevelHistoryEntryDto,
  PlanLevelHistoryQueryDto,
  PlanLevelHistorySortField,
} from '../../api/dto/plan-level-history.dto'
import { listPlanLevelHistory } from '../../api/plan-override-history-api'
import { useGetPlanBenefits } from '../PlanBenefitOverrides/hooks/useGetPlanBenefits'
import { PLAN_BENEFIT_OVERRIDE_RULE_PATTERN_FORMATTER } from '../PlanBenefitOverrides/components/rule-templates'
import { HIOS_PLAN_ID_REGEX } from '../PlanBenefitOverrides/components/planBenefitOverrideForm.schema'
import { useDeleteHistoryEntry } from '../../hooks/useDeleteHistoryEntry'
import { useDeleteHistoryByFilter } from '../../hooks/useDeleteHistoryByFilter'
import styles from '../../components/PlanOverridesComponents.module.scss'

const RULE_BASE = '/api/v1/plan-benefit-override-rules'

ModuleRegistry.registerModules([ServerSideRowModelModule])

const { RangePicker } = DatePicker

const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const ACTION_TAG_PROPS: Partial<
  Record<PlanOverrideAction, { color: string; label: string }>
> = {
  [PlanOverrideAction.OVERRIDE_CREATED]: { color: 'green', label: 'Created' },
  [PlanOverrideAction.OVERRIDE_UPDATED]: { color: 'blue', label: 'Updated' },
  [PlanOverrideAction.OVERRIDE_STATUS_CHANGED]: {
    color: 'orange',
    label: 'Status changed',
  },
  [PlanOverrideAction.OVERRIDE_DELETED]: { color: 'red', label: 'Deleted' },
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function formatActorName(name: string | null): string {
  if (!name) return '—'
  if (UUID_RE.test(name)) return `${name.slice(0, 8)}…`
  return name
}

const ActionCellRenderer = ({
  data,
}: ICellRendererParams<PlanLevelHistoryEntryDto>) => {
  if (!data) return null
  const known = ACTION_TAG_PROPS[data.action as PlanOverrideAction]
  const isDocument = (data.action as string).includes('DOCUMENT')
  const tagColor = known?.color ?? (isDocument ? 'purple' : 'default')
  const tagLabel = known?.label ?? (isDocument ? 'Document' : 'Other')
  return <Tag color={tagColor}>{tagLabel}</Tag>
}

const ActorCellRenderer = ({
  data,
}: ICellRendererParams<PlanLevelHistoryEntryDto>) => {
  if (!data) return null
  return (
    <span className={styles.historyActorCell}>
      {formatActorName(data.actorName)}
    </span>
  )
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: false,
  filter: false,
  resizable: true,
}

interface Props {
  onTotalChange?: (total: number) => void
  defaultPageSize?: number
  onPageSizeChange?: (size: number) => void
}

export default function PlanLevelHistoryContent({
  onTotalChange,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
}: Props) {
  const [hiosPlanIdInput, setHiosPlanIdInput] = useState('')
  const [benefitNameFilter, setBenefitNameFilter] = useState<
    string | undefined
  >(undefined)
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null)

  const debouncedHiosId = useDebounce(hiosPlanIdInput, SEARCH_DEBOUNCE_MS)
  const isHiosIdComplete =
    debouncedHiosId === '' || HIOS_PLAN_ID_REGEX.test(debouncedHiosId)
  const effectiveHiosId = HIOS_PLAN_ID_REGEX.test(debouncedHiosId)
    ? debouncedHiosId
    : undefined

  const filters = useMemo<Omit<PlanLevelHistoryQueryDto, 'page' | 'limit'>>(
    () => ({
      hiosPlanId: effectiveHiosId,
      benefitName: benefitNameFilter,
      occurredFrom: dateRange?.[0]?.toISOString() ?? undefined,
      occurredTo: dateRange?.[1]?.toISOString() ?? undefined,
    }),
    [effectiveHiosId, benefitNameFilter, dateRange]
  )

  const [total, setTotal] = useState(0)

  const onTotalChangeRef = useRef(onTotalChange)
  useEffect(() => {
    onTotalChangeRef.current = onTotalChange
  }, [onTotalChange])

  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  const [grid, setGrid] = useState<GridReadyEvent<PlanLevelHistoryEntryDto>>()
  const gridInitializedRef = useRef(false)

  const handleFirstDataRendered = useCallback(() => {
    gridInitializedRef.current = true
  }, [])

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<PlanLevelHistoryEntryDto>) => {
      if (event.newPageSize && gridInitializedRef.current) {
        onPageSizeChange?.(event.api.paginationGetPageSize())
      }
    },
    [onPageSizeChange]
  )

  const datasource = useMemo(
    () => ({
      getRows: async (
        params: IServerSideGetRowsParams<PlanLevelHistoryEntryDto>
      ) => {
        try {
          const { startRow, endRow, sortModel } = params.request
          const pageSize = (endRow ?? DEFAULT_PAGE_SIZE) - (startRow ?? 0)
          const page = Math.floor((startRow ?? 0) / pageSize) + 1
          const sort = (sortModel as SortModelItem[])?.[0]
          const sortBy = sort?.colId as PlanLevelHistorySortField | undefined
          const sortOrder = sort?.sort as 'asc' | 'desc' | undefined

          const result = await listPlanLevelHistory({
            ...filtersRef.current,
            page,
            limit: pageSize,
            ...(sortBy && sortOrder ? { sortBy, sortOrder } : {}),
          })

          onTotalChangeRef.current?.(result.meta.total)
          setTotal(result.meta.total)
          params.success({
            rowData: result.data,
            rowCount: result.meta.total,
          })
        } catch (error) {
          console.error('Failed to load plan level history', error)
          params.fail()
        }
      },
    }),
    [filters]
  )

  useEffect(() => {
    if (!grid) return
    grid.api.setGridOption('serverSideDatasource', datasource)
    grid.api.refreshServerSide({ purge: true })
  }, [grid, datasource])

  const { mutateAsync: deleteEntry } = useDeleteHistoryEntry(RULE_BASE)
  const { mutateAsync: deleteByFilter } = useDeleteHistoryByFilter(RULE_BASE)

  const handleDeleteRow = useCallback(
    (logId: string) => {
      Modal.confirm({
        title: 'Delete history entry?',
        content: 'This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          await deleteEntry(logId)
          grid?.api.refreshServerSide({ purge: true })
        },
      })
    },
    [deleteEntry, grid]
  )

  const handleDeleteByFilter = useCallback(() => {
    const hasFilters = Object.values(filtersRef.current).some(
      (v) => v !== undefined
    )
    Modal.confirm({
      title: 'Delete history entries?',
      content: hasFilters
        ? 'Delete all matching history entries? This action cannot be undone.'
        : 'Delete ALL history entries? This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteByFilter(filtersRef.current as Record<string, unknown>)
        grid?.api.refreshServerSide({ purge: true })
      },
    })
  }, [deleteByFilter, grid])

  const { benefits, benefitsByName } = useGetPlanBenefits()

  const benefitOptions = useMemo(
    () => benefits.map((b) => ({ value: b.name, label: b.displayName })),
    [benefits]
  )

  const handleRangeChange = useCallback(
    (values: [Dayjs | null, Dayjs | null] | null) => {
      setDateRange(values)
    },
    []
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<PlanLevelHistoryEntryDto>) => [
      {
        name: 'Copy Cell',
        action: () => {
          const value = params.value
          if (value != null) void navigator.clipboard.writeText(String(value))
        },
      },
    ],
    []
  )

  const columnDefs = useMemo<ColDef<PlanLevelHistoryEntryDto>[]>(
    () => [
      {
        headerName: 'Action',
        field: 'action',
        cellRenderer: ActionCellRenderer,
        width: 140,
      },
      {
        headerName: 'HIOS Plan ID',
        field: 'matchHiosPlanId',
        cellRenderer: ({ value }: ICellRendererParams) =>
          value ? <span>{value}</span> : null,
        width: 210,
      },
      {
        headerName: 'Benefit',
        field: 'matchBenefitName',
        valueGetter: (p) => {
          const name = p.data?.matchBenefitName
          if (!name) return ''
          return benefitsByName[name]?.displayName ?? name
        },
        cellRenderer: ({ value }: ICellRendererParams) => (
          <Tooltip title={value} placement="topLeft">
            <span className={styles.historyCellTruncated}>{value}</span>
          </Tooltip>
        ),
        flex: 2,
        minWidth: 180,
      },
      {
        headerName: 'Change to',
        valueGetter: (p) => {
          if (!p.data) return ''
          try {
            return PLAN_BENEFIT_OVERRIDE_RULE_PATTERN_FORMATTER[
              p.data.ruleTemplate
            ](p.data.params)
          } catch {
            return '—'
          }
        },
        cellRenderer: ({ value }: ICellRendererParams) => (
          <Tooltip title={value} placement="topLeft">
            <span className={styles.historyCellTruncated}>{value}</span>
          </Tooltip>
        ),
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: ({
          data: row,
        }: ICellRendererParams<PlanLevelHistoryEntryDto>) => {
          if (!row) return null
          return (
            <Tag
              color={
                row.status === 'APPROVED'
                  ? 'green'
                  : row.status === 'REJECTED'
                    ? 'red'
                    : 'gold'
              }
            >
              {row.status === 'APPROVED'
                ? 'Approved'
                : row.status === 'REJECTED'
                  ? 'Rejected'
                  : 'Draft'}
            </Tag>
          )
        },
        width: 110,
      },
      {
        headerName: 'Occurred',
        field: 'occurredAt',
        valueFormatter: (p) =>
          p.value ? relativeTimeString(p.value as string, 30) : '—',
        sort: 'desc',
        sortable: true,
        width: 150,
      },
      {
        headerName: 'Actor',
        cellRenderer: ActorCellRenderer,
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: '',
        colId: 'rowActions',
        width: 52,
        resizable: false,
        sortable: false,
        cellRenderer: ({
          data,
        }: ICellRendererParams<PlanLevelHistoryEntryDto>) => {
          if (!data) return null
          return (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteRow(data.id)}
            />
          )
        },
      },
    ],
    [benefitsByName, handleDeleteRow]
  )

  return (
    <>
      <div className={styles.historyFilterBar}>
        {total > 0 && (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDeleteByFilter}
            style={{ marginRight: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Tooltip
          title={
            !isHiosIdComplete
              ? 'Format: 5 digits + 2-letter state + 7 digits (e.g. 70525AR9000014)'
              : undefined
          }
          open={!isHiosIdComplete}
          placement="bottomLeft"
        >
          <Input
            placeholder="Search HIOS Plan ID"
            value={hiosPlanIdInput}
            allowClear
            maxLength={14}
            status={isHiosIdComplete ? undefined : 'warning'}
            className={styles.historyFilterInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const sanitized = e.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9]/g, '')
              setHiosPlanIdInput(sanitized)
            }}
          />
        </Tooltip>
        <Select
          placeholder="All benefits"
          allowClear
          className={styles.historyFilterSelect}
          options={benefitOptions}
          value={benefitNameFilter}
          onChange={(v: string | undefined) => {
            setBenefitNameFilter(v)
          }}
        />
        <RangePicker
          onChange={handleRangeChange}
          placeholder={['From', 'To']}
          className={styles.historyFilterDatePicker}
        />
      </div>

      <div className={`ag-theme-alpine ${styles.historyGridAutoHeight}`}>
        <AgGridReact
          getRowId={({ data }) => data.id}
          rowModelType="serverSide"
          columnDefs={columnDefs}
          defaultColDef={DEFAULT_COL_DEF}
          suppressRowClickSelection
          enableCellTextSelection
          getContextMenuItems={getContextMenuItems}
          overlayLoadingTemplate="Loading..."
          overlayNoRowsTemplate="No history entries yet"
          domLayout="autoHeight"
          rowHeight={52}
          pagination
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
          cacheBlockSize={defaultPageSize}
          onGridReady={setGrid}
          onFirstDataRendered={handleFirstDataRendered}
          onPaginationChanged={handlePaginationChanged}
        />
      </div>
    </>
  )
}
